<template>
  <div class="telegram-preview max-h-full bg-[#0e1621] relative pb-[65px]" :class="class">
    <div class="header bg-[#232e3c] p-3 flex items-center">
      <div class="flex items-center">
        <button @click="$emit('go-back')" class="text-[#8a9aa9] mr-3 size-5">
          <ArrowLeft class="text-[#8a9aa9] mr-3 size-5" />
        </button>
        <div class="avatar bg-[#5288c1] bg-[#e17076] rounded-full size-9 flex items-center justify-center text-white font-bold">
          {{ getInitials(sender || 'Bot') }}
        </div>
        <div class="ml-3">
          <h3 class="text-white font-medium">{{ sender || 'Bot' }}</h3>
          <p class="text-[#8a9aa9] text-xs">online</p>
        </div>
      </div>
      <div class="ml-auto flex gap-3">
        <button @click="toggleSearch" class="text-[#8a9aa9] size-5">
          <Search class="text-[#8a9aa9] size-5" />
        </button>
        <!-- <MoreVertical class="text-[#8a9aa9] size-5" /> -->
      </div>
    </div>
    
    <!-- Search bar (shown when search is active) -->
    <div v-if="isSearchActive" class="search-bar bg-[#232e3c] p-3 flex items-center gap-2">
      <button @click="toggleSearch" class="text-[#8a9aa9]">
        <ArrowLeft class="text-[#8a9aa9] size-5" />
      </button>
      <input 
        ref="searchInputRef"
        v-model="searchQuery" 
        class="bg-[#17212b] rounded-lg flex-1 py-2 px-4 text-white placeholder:text-[#8a9aa9] border-[unset] ring-[unset] focus:outline-none" 
        placeholder="Pesquisar..." 
        @input="handleSearch"
      />
      <button v-if="searchQuery" @click="clearSearch" class="text-[#8a9aa9]">
        <X class="text-[#8a9aa9] size-5" />
      </button>
      <div v-if="searchResults.length > 0" class="flex items-center gap-2">
        <span class="text-[#8a9aa9] text-xs">{{ currentResultIndex + 1 }}/{{ searchResults.length }}</span>
        <button @click="navigateToPrevResult" :disabled="searchResults.length === 0" class="text-[#8a9aa9] disabled:opacity-50">
          <ChevronUp class="text-[#8a9aa9] size-5" />
        </button>
        <button @click="navigateToNextResult" :disabled="searchResults.length === 0" class="text-[#8a9aa9] disabled:opacity-50">
          <ChevronDown class="text-[#8a9aa9] size-5" />
        </button>
      </div>
    </div>
    
    <div id="messages-container" ref="messagesContainerRef" class="messages-container max-h-[80dvh] md:max-h-[74dvh] overflow-auto bg-[#0e1621] p-4 min-h-[350px] overflow-y-auto flex flex-col-reverse">
      <div class="message-group flex flex-col gap-1">
        <!-- Message layout -->
        <div v-for="(group, groupIndex) in messageGroups" :key="`group-${groupIndex}`" 
             class="flex items-start gap-2" 
             :class="{ 'flex-row-reverse': isMessageFromAuthUser(group) }">
          <!-- Avatar (only shown for received messages) -->
          <div v-if="showAvatar && !isMessageFromAuthUser(group)" class="avatar-container mt-1 self-end">
            <div class="avatar bg-[#e17076] rounded-full size-9 flex items-center justify-center text-white font-bold">
              {{ getInitials(sender || 'Bot') }}
            </div>
          </div>
          
          <!-- Message content -->
          <div class="flex flex-col items-start gap-1 max-w-[80dvw] md:max-w-[23dvw]" 
               :class="{ 'items-end': isMessageFromAuthUser(group) }">
            <!-- Container for text+buttons group to ensure consistent width -->
            <div 
              v-if="group.type === 'text-button-group'" 
              class="text-button-group inline-flex flex-col"
              :id="`message-${groupIndex}`"
              :class="{ 'search-highlight': isHighlighted(groupIndex) }"
            >
              <!-- Text message in the group -->
              <div 
                v-if="group.textMessage"
                class="message-bubble p-3 text-sm break-words rounded-b-none w-full"
                :class="[
                  isMessageFromAuthUser(group)
                    ? 'bg-[#182533] text-white rounded-lg rounded-br-none' 
                    : 'bg-[#182533] rounded-lg rounded-b-none',
                  { 'text-blue-400': group.textMessage.copyable, 'text-white': !group.textMessage.copyable }
                ]"
              >
                <span class="break-all" v-html="highlightSearchText(group.textMessage.text || '')"></span>

                <!-- Time for text message -->
                <div class="absolute bottom-1 right-2 flex items-center gap-1">
                  <span class="text-[#8a9aa9] text-xs">
                    {{ formatTime(group.textMessage.sentAt ?? new Date()) }}
                  </span>
                  <Check class="text-[#8a9aa9] size-3" />
                </div>
              </div>
              
              <!-- Small gap between text and buttons -->
              <div class="h-[2px]"></div>
              
              <!-- Button messages in the group -->
              <div class="button-container">
                <button 
                  v-for="(buttonMsg, btnIndex) in group.buttonMessages" 
                  :key="`button-${btnIndex}`"
                  class="telegram-button font-normal py-2.5 px-3 text-center w-full text-sm relative"
                  :class="[
                    isMessageFromAuthUser(group) ? 'bg-[#222e3a] hover:bg-[#2c3b4b]' : 'bg-[#222e3a] hover:bg-[#2c3b4b]',
                    {
                      'rounded-t-none rounded-b-none mb-[2px]': btnIndex > 0 && btnIndex < group.buttonMessages.length - 1,
                      'rounded-t-none mb-[2px]': btnIndex === 0,
                      'rounded-b-md mb-[2px]': btnIndex === group.buttonMessages.length - 1
                    }
                  ]"
                >
                  <span v-html="highlightSearchText(buttonMsg.text || '')"></span>
                  <ArrowUpRight v-if="buttonMsg.isRedirectLink" class="size-3 absolute right-1 top-1 text-white" />
                </button>
              </div>
            </div>
            
              <!-- Flow trigger message -->
              <div 
                v-else-if="group.message.type === 'flow'"
                class="flow-bubble p-4 text-sm relative break-words min-w-[240px] border border-primary/20 bg-gradient-to-br from-[#182533] to-[#1e2a38] rounded-lg text-white"
                :id="`message-${groupIndex}`"
              >
                <div class="flex items-start gap-3">
                  <div class="bg-primary/20 rounded-xl p-2.5 text-primary flex-shrink-0 animate-pulse">
                      <GitMerge class="size-6" />
                  </div>
                  <div class="flex-1 overflow-hidden">
                    <p class="text-[10px] uppercase tracking-wider text-primary/70 font-bold mb-1">Gatilho de Fluxo</p>
                    <p class="text-white text-sm font-semibold truncate">{{ group.message.text }}</p>
                    <p v-if="group.message.description" class="text-zinc-400 text-[11px] line-clamp-2 mt-1">{{ group.message.description }}</p>
                  </div>
                </div>
                
                <div class="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] text-zinc-500">
                  <span class="flex items-center gap-1"><Zap class="size-3 text-yellow-500" /> Automático</span>
                  <span>{{ formatTime(group.message.sentAt ?? new Date()) }}</span>
                </div>
              </div>

              <!-- Individual messages not in a group -->
              <template v-else>
              <!-- Image message -->
              <div 
                v-if="group.message.type === 'image' && group.message.source"
                class="media-bubble overflow-hidden relative w-full"
                :id="`message-${groupIndex}`"
                :class="[
                  isMessageFromAuthUser(group)
                    ? 'bg-[#182533] rounded-lg rounded-br-none' 
                    : 'bg-[#182533] rounded-lg'
                ]"
              >
                <img 
                  :src="group.message.source" 
                  alt="Image" 
                  class="w-full max-h-[330px] object-cover"
                />
              </div>
              
              <!-- Video message -->
              <div 
                v-else-if="group.message.type === 'video' && group.message.source"
                class="media-bubble overflow-hidden relative w-full"
                :id="`message-${groupIndex}`"
                :class="[
                  isMessageFromAuthUser(group)
                    ? 'bg-[#182533] rounded-lg rounded-br-none' 
                    : 'bg-[#182533] rounded-lg'
                ]"
              >
                <video 
                  :src="group.message.source" 
                  controls 
                  class="w-full max-h-[200px] object-cover"
                />
              </div>

              <!-- Audio message -->
              <div 
                v-else-if="group.message.type == 'audio'"
                class="media-bubble overflow-hidden relative w-full"
                :id="`message-${groupIndex}`"
                :class="[
                  isMessageFromAuthUser(group)
                    ? 'bg-[#182533] rounded-lg rounded-br-none' 
                    : 'bg-[#182533] rounded-lg'
                ]"
              >
                <div class="p-3 flex items-center gap-2">
                  <button 
                    @click="toggleAudioPlay(group.message.source as string)"
                    class="bg-[#5288c1] rounded-full size-8 flex items-center justify-center"
                  >
                    <Play v-if="!isAudioPlaying(group.message.source as string)" class="size-4 text-white" />
                    <Pause v-else class="size-4 text-white" />
                  </button>
                  <div class="flex-1">
                    <div class="h-1 bg-[#8a9aa9]/30 rounded-full w-full">
                      <div 
                        class="h-1 bg-[#5288c1] rounded-full" 
                        :style="{ width: getAudioProgress(group.message.source as string) + '%' }"
                      ></div>
                    </div>
                  </div>
                  <span class="text-[#8a9aa9] text-xs">
                    {{ formatAudioTime(getAudioCurrentTime(group.message.source as string)) }}
                  </span>
                </div>
              </div>

              <!-- Text message (standalone) -->
              <div 
                v-else-if="group.message.type === 'text'"
                class="message-bubble p-3 text-sm relative break-words"
                :id="`message-${groupIndex}`"
                :class="[
                  isMessageFromAuthUser(group)
                    ? 'bg-[#182533] rounded-lg rounded-br-none' 
                    : 'bg-[#182533] rounded-lg',
                  { 'text-blue-400': group.message.copyable, 'text-white': !group.message.copyable },
                  { 'search-highlight': isHighlighted(groupIndex) }
                ]"
              >
                <span class="break-all" v-html="highlightSearchText(group.message.text || '')"></span>

                <!-- Time for ALL text messages -->
                <div class="absolute bottom-1 right-2 flex items-center gap-1">
                  <span class="text-[#8a9aa9] text-xs">
                    {{ formatTime(group.message.sentAt ?? new Date()) }}
                  </span>
                  <Check class="text-[#8a9aa9] size-3" />
                </div>
              </div>
              
              <!-- Button message (standalone) -->
              <button 
                v-else-if="group.message.type === 'button'"
                class="telegram-button font-normal py-2.5 px-8 rounded-md text-center text-sm relative"
                :id="`message-${groupIndex}`"
                :class="[
                  isMessageFromAuthUser(group) ? 'bg-[#222e3a] hover:bg-[#2c3b4b]' : 'bg-[#222e3a] hover:bg-[#2c3b4b]',
                  { 'search-highlight': isHighlighted(groupIndex) }
                ]"
              >
                <span v-html="highlightSearchText(group.message.text || '')"></span>
                <ArrowUpRight v-if="group.message.isRedirectLink" class="size-3 absolute right-1 top-1 text-white" />
              </button>
              <!-- Document message (standalone) -->
              <div 
                v-else-if="group.message.type === 'document' && group.message.source"
                class="message-bubble p-3 text-sm relative break-words min-w-[200px]"
                :id="`message-${groupIndex}`"
                :class="[
                  isMessageFromAuthUser(group)
                    ? 'bg-[#182533] rounded-lg rounded-br-none' 
                    : 'bg-[#182533] rounded-lg',
                   { 'search-highlight': isHighlighted(groupIndex) }
                ]"
              >
                <div class="flex items-center gap-3">
                  <div class="bg-[#2b5278] rounded-full p-2 text-white flex-shrink-0">
                      <FileText class="size-6" />
                  </div>
                  <div class="flex-1 overflow-hidden">
                    <p class="text-white text-sm truncate font-medium">Arquivo</p>
                    <a :href="group.message.source" target="_blank" class="text-[#64b3f4] text-xs hover:underline truncate block">Baixar Documento</a>
                  </div>
                </div>
                
                <div class="absolute bottom-1 right-2 flex items-center gap-1">
                  <span class="text-[#8a9aa9] text-xs">
                    {{ formatTime(group.message.sentAt ?? new Date()) }}
                  </span>
                  <Check class="text-[#8a9aa9] size-3" />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    
    <div class="input-area bg-[#17212b] border-t border-[#232e3c] p-3 flex items-center absolute bottom-0 w-full">
      <Paperclip v-if="!disableAttachments" class="text-[#8a9aa9] size-5 mr-3" />
      <input 
        v-if="!isRecording" 
        :disabled="disableInput" 
        @keydown.enter="$emit('send-message', ($event.target as HTMLInputElement).value); ($event.target as HTMLInputElement).value = ''" 
        class="bg-[#232e3c] rounded-lg flex-1 py-2 px-4 placeholder:text-[#8a9aa9] border-[unset] ring-[unset] focus:outline-none" 
        placeholder="Mensagem" 
      />
      <div v-else class="flex-1 bg-[#232e3c] rounded-lg py-2 px-4 flex items-center gap-2">
        <div class="recording-indicator flex items-center gap-2">
          <div class="size-2 rounded-full bg-red-500 animate-pulse"></div>
          <span class="text-[#8a9aa9]">{{ formatAudioTime(recordingTime) }}</span>
        </div>
        <div class="flex-1">
          <div class="h-1 bg-[#8a9aa9]/30 rounded-full w-full">
            <div class="h-1 bg-red-500 rounded-full recording-progress"></div>
          </div>
        </div>
      </div>
      <button 
        v-if="!disableMic" 
        @click="toggleRecording" 
        class="ml-3 flex items-center justify-center"
        :class="{ 'text-red-500': isRecording, 'text-[#8a9aa9]': !isRecording }"
      >
        <Mic v-if="!isRecording" class="size-5" />
        <Square v-else class="size-5" />
      </button>
    </div>
    
    <!-- Hidden audio elements for playback -->
    <audio v-for="(audio, index) in audioElements" :key="index" :src="audio.src" ref="audioRefs"></audio>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, onBeforeUnmount, watch } from 'vue'
import { ArrowLeft, Search, MoreVertical, Check, Paperclip, Mic, ArrowUpRight, X, ChevronUp, ChevronDown, Play, Pause, Square, FileText, Zap, GitMerge } from 'lucide-vue-next'

// Define the MessagePreviewProp interface
export interface MessagePreviewProp {
  type: 'text' | 'button' | 'video' | 'image' | 'audio' | 'document' | 'flow'
  text?: string
  description?: string
  source?: string
  isRedirectLink?: boolean
  copyable?: boolean
  isFromAuthUser?: boolean
  sentAt?: string | Date
}

// Define the MessageGroup type
type TextButtonGroup = {
  type: 'text-button-group'
  textMessage: MessagePreviewProp
  buttonMessages: MessagePreviewProp[]
}

type IndividualMessage = {
  type: 'individual'
  message: MessagePreviewProp
}

type MessageGroup = TextButtonGroup | IndividualMessage

type SearchResult = {
  groupIndex: number
  text: string
}

type AudioElement = {
  src: string
  isPlaying: boolean
  currentTime: number
  duration: number
  progress: number
}

const props = defineProps({
  messages: {
    type: Array as () => MessagePreviewProp[],
    default: () => []
  },
  sender: {
    type: String,
    default: 'Bot'
  },
  sentAt: {
    type: [String, Date],
    default: () => new Date()
  },
  showAvatar: {
    type: Boolean,
    default: true
  },
  class: {
    type: String,
    default: ''
  },
  disableInput: {
    type: Boolean,
    default: false
  },
  disableMic: {
    type: Boolean,
    default: false
  },
  disableAttachments: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['send-message', 'go-back', 'audio-recorded'])

// Search functionality
const isSearchActive = ref(false)
const searchQuery = ref('')
const searchResults = ref<SearchResult[]>([])
const currentResultIndex = ref(0)
const messagesContainerRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)

// Audio recording functionality
const isRecording = ref(false)
const mediaRecorder = ref<MediaRecorder | null>(null)
const audioChunks = ref<BlobPart[]>([])
const recordingTime = ref(0)
const recordingTimer = ref<number | null>(null)
const audioElements = ref<AudioElement[]>([])
const audioRefs = ref<HTMLAudioElement[]>([])

// Toggle search visibility
const toggleSearch = () => {
  isSearchActive.value = !isSearchActive.value
  if (isSearchActive.value) {
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  } else {
    clearSearch()
  }
}

// Clear search
const clearSearch = () => {
  searchQuery.value = ''
  searchResults.value = []
  currentResultIndex.value = 0
}

// Handle search input
const handleSearch = () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = []
    return
  }
  
  const results: SearchResult[] = []
  
  // Search through all message groups
  messageGroups.value.forEach((group, groupIndex) => {
    if (group.type === 'text-button-group') {
      // Search in text message
      if (group.textMessage.text && group.textMessage.text.toLowerCase().includes(searchQuery.value.toLowerCase())) {
        results.push({
          groupIndex,
          text: group.textMessage.text
        })
      }
      
      // Search in button messages
      group.buttonMessages.forEach(buttonMsg => {
        if (buttonMsg.text && buttonMsg.text.toLowerCase().includes(searchQuery.value.toLowerCase())) {
          results.push({
            groupIndex,
            text: buttonMsg.text
          })
        }
      })
    } else if (group.type === 'individual' && group.message.text) {
      // Search in individual message
      if (group.message.text.toLowerCase().includes(searchQuery.value.toLowerCase())) {
        results.push({
          groupIndex,
          text: group.message.text
        })
      }
    }
  })
  
  searchResults.value = results
  currentResultIndex.value = results.length > 0 ? 0 : -1
  
  // Scroll to first result if found
  if (results.length > 0) {
    scrollToResult(0)
  }
}

// Navigate to previous search result
const navigateToPrevResult = () => {
  if (searchResults.value.length === 0) return
  
  currentResultIndex.value = (currentResultIndex.value - 1 + searchResults.value.length) % searchResults.value.length
  scrollToResult(currentResultIndex.value)
}

// Navigate to next search result
const navigateToNextResult = () => {
  if (searchResults.value.length === 0) return
  
  currentResultIndex.value = (currentResultIndex.value + 1) % searchResults.value.length
  scrollToResult(currentResultIndex.value)
}

// Scroll to a specific search result
const scrollToResult = (index: number) => {
  if (!messagesContainerRef.value) return
  
  const resultGroupIndex = searchResults.value[index].groupIndex
  const messageElement = document.getElementById(`message-${resultGroupIndex}`)
  
  if (messageElement) {
    messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// Check if a message group is highlighted (current search result)
const isHighlighted = (groupIndex: number): boolean => {
  if (searchResults.value.length === 0 || currentResultIndex.value === -1) return false
  return searchResults.value[currentResultIndex.value].groupIndex === groupIndex
}

// Highlight search text in message content
const highlightSearchText = (text: string): string => {
  if (!searchQuery.value.trim() || !text) return text
  
  const regex = new RegExp(`(${escapeRegExp(searchQuery.value)})`, 'gi')
  return text.replace(regex, '<span class="search-text-highlight">$1</span>')
}

// Escape special characters in string for use in regex
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Helper function to check if a message group is from authenticated user
const isMessageFromAuthUser = (group: MessageGroup): boolean => {
  if (group.type === 'text-button-group') {
    return group.textMessage.isFromAuthUser || false
  }
  return group.message.isFromAuthUser || false
}

// Group messages to handle text+button combinations
const messageGroups = computed<MessageGroup[]>(() => {
  const groups: MessageGroup[] = [];
  let i = 0;
  
  while (i < props.messages.length) {
    const message = props.messages[i];
    
    // Check if this is a text message followed by button(s)
    if (message.type === 'text') {
      // Look ahead to see if there are buttons following this text
      let j = i + 1;
      const buttonMessages: MessagePreviewProp[] = [];
      
      while (j < props.messages.length && props.messages[j].type === 'button') {
        buttonMessages.push(props.messages[j]);
        j++;
      }
      
      // If we found buttons, create a text-button group
      if (buttonMessages.length > 0) {
        groups.push({
          type: 'text-button-group',
          textMessage: message,
          buttonMessages
        });
        
        // Skip the processed messages
        i = j;
        continue;
      }
    }
    
    // If not part of a group, add as individual message
    groups.push({
      type: 'individual',
      message
    });
    
    i++;
  }
  
  return groups;
});

const isLastMessage = (message: MessagePreviewProp): boolean => {
  return props.messages[props.messages.length - 1] === message;
};

const getInitials = (name: string): string => {
  if (!name) return 'B'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
}

const formatTime = (timestamp: string | Date): string => {
  if (!timestamp) return '00:59'
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
}

// Format audio time (seconds to MM:SS)
const formatAudioTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Toggle audio recording
const toggleRecording = async () => {
  if (isRecording.value) {
    stopRecording()
  } else {
    await startRecording()
  }
}

// Start audio recording
const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    
    mediaRecorder.value = new MediaRecorder(stream)
    audioChunks.value = []
    recordingTime.value = 0
    
    mediaRecorder.value.ondataavailable = (event) => {
      audioChunks.value.push(event.data)
    }
    
    mediaRecorder.value.onstop = () => {
      const audioBlob = new Blob(audioChunks.value, { type: 'audio/webm' })
      const audioUrl = URL.createObjectURL(audioBlob)
      
      // Emit the recorded audio to parent component
      emit('audio-recorded', {
        blob: audioBlob,
        url: audioUrl,
        duration: recordingTime.value
      })
      
      // Create a message with the audio
      const audioMessage: MessagePreviewProp = {
        type: 'audio',
        source: audioUrl,
        isFromAuthUser: true,
        sentAt: new Date().toISOString()
      }
      
      // Add to audio elements for playback
      audioElements.value.push({
        src: audioUrl,
        isPlaying: false,
        currentTime: 0,
        duration: recordingTime.value,
        progress: 0
      })
      
      // Emit send-message event with the audio message
      emit('send-message', audioMessage)
      
      // Stop all tracks
      stream.getTracks().forEach(track => track.stop())
    }
    
    // Start recording
    mediaRecorder.value.start()
    isRecording.value = true
    
    // Start timer
    recordingTimer.value = window.setInterval(() => {
      recordingTime.value += 1
    }, 1000)
    
  } catch (error) {
    console.error('Error accessing microphone:', error)
  }
}

// Stop audio recording
const stopRecording = () => {
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
    mediaRecorder.value.stop()
    isRecording.value = false
    
    // Clear timer
    if (recordingTimer.value) {
      clearInterval(recordingTimer.value)
      recordingTimer.value = null
    }
  }
}

// Audio playback functions
const toggleAudioPlay = (src: string) => {
  const audioIndex = audioElements.value.findIndex(audio => audio.src === src)
  if (audioIndex === -1) return
  
  const audio = audioRefs.value[audioIndex]
  if (!audio) return
  
  if (audioElements.value[audioIndex].isPlaying) {
    audio.pause()
    audioElements.value[audioIndex].isPlaying = false
  } else {
    // Pause all other playing audio
    audioElements.value.forEach((a, i) => {
      if (i !== audioIndex && a.isPlaying) {
        audioRefs.value[i]?.pause()
        a.isPlaying = false
      }
    })
    
    audio.play()
    audioElements.value[audioIndex].isPlaying = true
  }
}

const isAudioPlaying = (src: string): boolean => {
  const audioIndex = audioElements.value.findIndex(audio => audio.src === src)
  if (audioIndex === -1) return false
  return audioElements.value[audioIndex].isPlaying
}

const getAudioProgress = (src: string): number => {
  const audioIndex = audioElements.value.findIndex(audio => audio.src === src)
  if (audioIndex === -1) return 0
  return audioElements.value[audioIndex].progress
}

const getAudioCurrentTime = (src: string): number => {
  const audioIndex = audioElements.value.findIndex(audio => audio.src === src)
  if (audioIndex === -1) return 0
  return audioElements.value[audioIndex].currentTime
}

// Update audio progress
const updateAudioProgress = () => {
  audioRefs.value.forEach((audio, index) => {
    if (!audio) return
    
    audioElements.value[index].currentTime = audio.currentTime
    audioElements.value[index].duration = audio.duration || 0
    audioElements.value[index].progress = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0
    
    // Check if audio has ended
    if (audio.ended) {
      audioElements.value[index].isPlaying = false
    }
  })
}

// Add keyboard shortcuts for search
onMounted(() => {
  window.addEventListener('keydown', (e) => {
    // Ctrl+F or Cmd+F to open search
    if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
      e.preventDefault()
      toggleSearch()
    }
    
    // Escape to close search
    if (e.key === 'Escape' && isSearchActive.value) {
      toggleSearch()
    }
    
    // Enter to navigate to next result
    if (e.key === 'Enter' && isSearchActive.value) {
      navigateToNextResult()
    }
    
    // Shift+Enter to navigate to previous result
    if (e.key === 'Enter' && e.shiftKey && isSearchActive.value) {
      navigateToPrevResult()
    }
  })
  
  // Set up audio progress update interval
  const progressInterval = setInterval(updateAudioProgress, 100)
  
  onBeforeUnmount(() => {
    clearInterval(progressInterval)
    
    // Clean up recording if active
    if (isRecording.value) {
      stopRecording()
    }
    
    // Release object URLs
    audioElements.value.forEach(audio => {
      URL.revokeObjectURL(audio.src)
    })
  })
})

// Add messages to audio elements when they're added
const updateAudioElementsFromMessages = () => {
  props.messages.forEach(message => {
    if (message.type === 'audio' && message.source) {
      // Check if this audio source is already in our elements
      const exists = audioElements.value.some(audio => audio.src === message.source)
      if (!exists) {
        audioElements.value.push({
          src: message.source,
          isPlaying: false,
          currentTime: 0,
          duration: 0,
          progress: 0
        })
      }
    }
  })
}

// Watch for new messages with audio
watch(() => props.messages, () => {
  updateAudioElementsFromMessages()
}, { deep: true })

// Initialize audio elements from existing messages
updateAudioElementsFromMessages()
</script>

<style>
.telegram-preview {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.messages-container {
  background-color: #0e1621;
}

.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: #0e1621;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #232e3c;
  border-radius: 3px;
}

.telegram-button {
  border: none;
  transition: background-color 0.2s;
  color: white;
}

.message-bubble {
  padding-right: 4rem;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

.message-bubble b, .message-bubble strong { font-weight: bold; }
.message-bubble i, .message-bubble em { font-style: italic; }
.message-bubble s, .message-bubble strike, .message-bubble del { text-decoration: line-through; }
.message-bubble u, .message-bubble ins { text-decoration: underline; }
.message-bubble code { font-family: monospace; background: rgba(255,255,255,0.1); padding: 0 2px; border-radius: 2px; }
.message-bubble tg-spoiler { 
  background-color: rgba(255,255,255,0.1);
  filter: blur(4px);
  transition: filter 0.3s;
  cursor: pointer;
}
.message-bubble tg-spoiler:hover {
  filter: blur(0);
}

.media-bubble {
  max-width: 300px;
}

.flow-bubble {
  padding-right: 1rem !important;
}

.text-button-group {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
}

.button-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* Search styles */
.search-bar {
  transition: all 0.3s ease;
}

.search-highlight {
  box-shadow: 0 0 0 2px #5288c1;
}

:deep(.search-text-highlight) {
  background-color: #5288c1;
  color: white;
  border-radius: 2px;
  padding: 0 2px;
}

/* Recording animation */
.recording-progress {
  animation: recording-pulse 2s infinite;
}

@keyframes recording-pulse {
  0% {
    width: 0%;
  }
  50% {
    width: 100%;
  }
  100% {
    width: 0%;
  }
}
</style>